import { Fragment, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../components/AuthProvider';

const navigationItems = [
  { name: 'Home', path: '/home' },
  { name: 'Personal', path: '/personal' },
  { name: 'Pilates', path: '/pilates' },
  { name: 'Statistiche', path: '/Statistiche', requiresPin: true },
]

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [showPinModal, setShowPinModal] = useState(false)
  const [pinInput, setPinInput] = useState('')
  const [pinError, setPinError] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { signOut } = useAuth()

  const handleNavigation = (item) => {
    if (item.requiresPin) {
      setShowPinModal(true)
      setPinInput('')
      setPinError(false)
    } else {
      navigate(item.path)
    }
  }

  const handlePinSubmit = (e) => {
    e.preventDefault()
    if (pinInput.toLowerCase() === 'raff') {
      setShowPinModal(false)
      setPinInput('')
      setPinError(false)
      navigate('/Statistiche')
      setMobileMenuOpen(false)
    } else {
      setPinError(true)
      setPinInput('')
    }
  }

  const isActive = (path) => location.pathname === path

  const showBackButton = location?.pathname?.split("/")[1] === "profiloUtente"

  if (location.pathname === "/") return null

  return (
    <div className='sticky top-0 z-50 w-full flex items-center justify-center bg-zinc-50/80 backdrop-blur-md border-b border-zinc-200'>
      <header className="w-full px-4 sm:px-6 lg:px-8">
        <nav className="flex items-center justify-between h-16" aria-label="Navigazione principale">
          
          {/* Left section - Back button or Logo */}
          <div className="flex items-center min-w-0 flex-1">
            {showBackButton ? (
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 text-sm font-medium text-zinc-700 hover:text-zinc-900 transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 rounded-lg px-3 py-2"
                aria-label="Torna alla pagina precedente"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="hidden sm:inline">Indietro</span>
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-yellow-400 to-yellow-500 flex items-center justify-center shadow-sm">
                  <span className="text-zinc-900 font-bold text-lg">P</span>
                </div>
                <span className="hidden sm:inline text-lg font-bold text-zinc-900">Pale</span>
              </div>
            )}
          </div>

          {/* Center section - Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1 flex-shrink-0">
            {navigationItems.map((item) => (
              <button
                key={item.path}
                onClick={() => handleNavigation(item)}
                className={`
                  relative px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200
                  ${isActive(item.path)
                    ? 'text-zinc-900 bg-yellow-400 shadow-sm'
                    : 'text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100'
                  }
                  focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2
                `}
                aria-current={isActive(item.path) ? 'page' : undefined}
              >
                {item.name}
              </button>
            ))}
          </div>

          {/* Right section - Logout & Mobile menu */}
          <div className="flex items-center justify-end gap-4 min-w-0 flex-1">
            {/* Desktop logout button */}
            <button
              onClick={() => signOut()}
              className="hidden lg:inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-zinc-700 hover:text-zinc-900 transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 rounded-lg"
              aria-label="Esci dall'applicazione"
            >
              <span>Esci</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>

            {/* Mobile menu button */}
            <button
              type="button"
              className="lg:hidden inline-flex items-center justify-center p-2 rounded-lg text-zinc-700 hover:text-zinc-900 hover:bg-zinc-100 focus:outline-none focus:ring-2 focus:ring-yellow-400"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Apri menu di navigazione"
            >
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        </nav>

        {/* Mobile menu */}
        <Transition.Root show={mobileMenuOpen} as={Fragment}>
          <Dialog as="div" className="lg:hidden relative z-50" onClose={setMobileMenuOpen}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-zinc-900/40 backdrop-blur-sm" />
            </Transition.Child>

            <div className="fixed inset-0 flex justify-end">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in duration-200"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="relative w-full max-w-xs bg-white shadow-2xl">
                  <div className="flex flex-col h-full">
                    {/* Mobile menu header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-yellow-400 to-yellow-500 flex items-center justify-center shadow-sm">
                          <span className="text-zinc-900 font-bold text-lg">P</span>
                        </div>
                        <span className="text-lg font-bold text-zinc-900">Menu</span>
                      </div>
                      <button
                        type="button"
                        className="p-2 rounded-lg text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                        onClick={() => setMobileMenuOpen(false)}
                        aria-label="Chiudi menu"
                      >
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>

                    {/* Mobile navigation items */}
                    <div className="flex-1 px-6 py-6 overflow-y-auto">
                      <nav className="space-y-2" aria-label="Navigazione mobile">
                        {navigationItems.map((item) => (
                          <button
                            key={item.path}
                            onClick={() => {
                              if (item.requiresPin) {
                                handleNavigation(item)
                              } else {
                                navigate(item.path)
                                setMobileMenuOpen(false)
                              }
                            }}
                            className={`
                              w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-all
                              ${isActive(item.path)
                                ? 'bg-yellow-400 text-zinc-900 shadow-sm'
                                : 'text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900'
                              }
                              focus:outline-none focus:ring-2 focus:ring-yellow-400
                            `}
                            aria-current={isActive(item.path) ? 'page' : undefined}
                          >
                            {item.name}
                          </button>
                        ))}
                      </nav>
                    </div>

                    {/* Mobile logout button */}
                    <div className="px-6 py-4 border-t border-zinc-200">
                      <button
                        onClick={() => {
                          signOut()
                          setMobileMenuOpen(false)
                        }}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-base font-medium text-red-600 hover:bg-red-50 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
                        aria-label="Esci dall'applicazione"
                      >
                        <span>Esci</span>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>

        {/* PIN Modal */}
        <Transition.Root show={showPinModal} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={setShowPinModal}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-zinc-900/60 backdrop-blur-sm transition-opacity" />
            </Transition.Child>

            <div className="fixed inset-0 z-10 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white p-6 shadow-2xl transition-all w-full max-w-sm">
                    <div className="text-center">
                      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
                        <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                        </svg>
                      </div>
                      <Dialog.Title as="h3" className="mt-4 text-lg font-semibold text-zinc-900">
                        Accesso Riservato
                      </Dialog.Title>
                      <p className="mt-2 text-sm text-zinc-600">
                        Inserisci il PIN per accedere alle Statistiche
                      </p>
                      
                      <form onSubmit={handlePinSubmit} className="mt-4">
                        <input
                          type="password"
                          value={pinInput}
                          onChange={(e) => {
                            setPinInput(e.target.value)
                            setPinError(false)
                          }}
                          placeholder="Inserisci PIN"
                          autoFocus
                          className={`w-full px-4 py-3 text-center text-lg font-medium border-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2
                            ${pinError 
                              ? 'border-red-300 bg-red-50 text-red-900 focus:ring-red-500' 
                              : 'border-zinc-300 focus:border-yellow-400 focus:ring-yellow-400'
                            }
                          `}
                        />
                        {pinError && (
                          <p className="mt-2 text-sm text-red-600 font-medium">
                            PIN errato. Riprova.
                          </p>
                        )}
                        
                        <div className="mt-5 flex gap-3">
                          <button
                            type="button"
                            onClick={() => {
                              setShowPinModal(false)
                              setPinInput('')
                              setPinError(false)
                            }}
                            className="flex-1 px-4 py-2.5 text-sm font-medium text-zinc-700 bg-zinc-100 hover:bg-zinc-200 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zinc-400"
                          >
                            Annulla
                          </button>
                          <button
                            type="submit"
                            className="flex-1 px-4 py-2.5 text-sm font-medium text-zinc-900 bg-yellow-400 hover:bg-yellow-500 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-400 shadow-sm"
                          >
                            Conferma
                          </button>
                        </div>
                      </form>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition.Root>
      </header>
    </div>
  )
}
